import isEqual from 'lodash/isEqual';
import {ParamComponent} from 'panel';
import {VirtualElement} from '@floating-ui/dom';

import {InsightsBookmarkParams, InsightsQueryResults} from 'common/report/insights/util/types';
import {DOWNLOAD_ROW_LIMIT} from 'common/report/insights/util/chart/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {useStore} from 'common/util/component-helpers';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getProjectId, getWorkspaceId, isPublicMode} from 'common/report/util';
import {SortConfig} from 'common/report/util/sorting/types';
import {
  CellClickEvent,
  ColumnResizeEvent,
  Columns,
  ColumnWidthOverrides,
  CommonDataTableFields,
  SegmentMetadataNode,
  SortChangeEvent,
  TableColumnType,
  TableRow,
  Theme,
} from 'common/components/data-table/types';
import {Themes} from 'common/components/data-table/themes';
import {IronContextRegistry} from 'common/report/context-registry';
import type {PopoverActionCallback, PopoverCallback} from 'common/charts/types';
import {flattenColumns} from 'common/components/data-table/utils';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProject} from 'common/report/model-resources/projects';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';

import {template} from './template';
import css from './index.inline.styl';
import {getTableData, InsightsTableColumnContext} from './utils';

interface InsightsTableState {
  rows: TableRow[];
  columnGroups: Columns<any, InsightsTableColumnContext>;
  segmentMetadata?: SegmentMetadataNode;
  sortConfig: SortConfig;
  segmentationLimit: number;
  isDownloadTruncated: boolean;
  /** when set, identifies a click event to a specific metric column */
  popoverState: {
    metricKey: string | null;
    isTimeComparisonMetric: boolean;
    path: string[];
    targetCell?: HTMLElement;
    columnType: TableColumnType;
    columnIndex?: number;
    rowIndex?: number;
    virtualElementPosition: VirtualElement;
  } | null;
}
interface InsightsTableParams {
  canUseViewN?: boolean;
  bookmarkParams: InsightsBookmarkParams | null;
  results: InsightsQueryResults | null;
  /** dataGroups will automatically be obtained from Stores without this param,
   *  but this param is available for environments where Stores aren't supported (eg. Public Dashboards) */
  dataGroups?: Record<string, DataGroup>;
  downloadMode?: boolean;
  resizingLayout?: boolean;
  startScrolledRight?: boolean;

  /** provides actionable items on clicked table cells */
  popoverCallback?: PopoverCallback;
  popoverActionCallback?: PopoverActionCallback;

  sortChangeCallback?: (options: SortChangeEvent[`detail`]) => void;
  columnResizeCallback?: (ev: ColumnResizeEvent) => void;
  columnWidthOverrides?: ColumnWidthOverrides;
}
export interface InsightsTableJsxProps {
  params: InsightsTableParams;
  on?: EmptyObj;
}

export class InsightsTableChart extends ParamComponent<
  InsightsTableJsxProps['params'],
  InsightsTableState,
  IronContextRegistry
> {
  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        rows: null,
        columnGroups: null,
        sortConfig: null,
        segmentationLimit: null,
        isDownloadTruncated: false,
        popoverState: null,
      },
      defaultParams: {
        bookmarkParams: null,
        results: null,
        resizingLayout: false,
        startScrolledRight: false,
        columnWidthOverrides: {},
      },
      params: {
        canUseViewN: Boolean,
        bookmarkParams: {
          type: Object,
          required: true,
        },
        results: {
          type: Object,
          required: true,
        },
        dataGroups: {
          type: Object,
        },
        downloadMode: Boolean,
        resizingLayout: {
          type: Boolean,
        },
        startScrolledRight: Boolean,
        popoverCallback: Function,
        popoverActionCallback: Function,
        sortChangeCallback: Function,
        columnResizeCallback: Function,
        columnWidthOverrides: Object,
      },
      contexts: [
        `currentProject` as const,
        `segmentFilterController` as const,
        `metrics` as const,
        `behaviors` as const,
      ],
      defaultContexts: {
        metrics: new MetricsResource(),
        behaviors: new BehaviorsResource(),
      },
      hooks: {
        preUpdate: (_newState, newParams?: InsightsTableParams) => {
          if (
            newParams &&
            (!isEqual(newParams.bookmarkParams, this.params.bookmarkParams) ||
              !isEqual(newParams.results, this.params.results) ||
              !isEqual(newParams.dataGroups, this.params.dataGroups))
          ) {
            this.updateChartData(newParams);
          }
        },
      },
    };
  }

  resourceCustomSubscriptions = new Map([[MetricsResource, () => this.updateChartData(this.params)]]);

  connectedCallback() {
    super.connectedCallback();
    if (!this.params.dataGroups) {
      useStore(this, dataGroupStore).fetch({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    }

    this.updateChartData(this.params);
  }

  get currentProject(): CurrentProject | undefined {
    return isPublicMode() ? undefined : this.getContext(`currentProject`).get().value;
  }

  get segmentFilterController() {
    return this.getContext(`segmentFilterController`);
  }

  hasRequiredParams(params: InsightsTableParams): boolean {
    return !!(this.isConnected && params.bookmarkParams && params.results);
  }

  updateChartData(params: InsightsTableParams) {
    if (this.hasRequiredParams(params)) {
      const {bookmarkParams} = params;
      const dataGroups = params.dataGroups || dataGroupStore.value;

      const {columnGroups, rows, segmentMetadata, defaultSortConfig, sortConfig} = getTableData({
        results: params.results,
        bookmarkParams,
        dataGroups,
        canUseViewN: params.canUseViewN,
        metricResource: this.getContext(`metrics`),
        behaviorResource: this.getContext(`behaviors`),
      });
      const segmentationLimit = params.results.meta.queryLimits?.limit;

      let downloadTruncatedRows;
      if (params.downloadMode && rows.length > DOWNLOAD_ROW_LIMIT) {
        downloadTruncatedRows = rows.slice(0, DOWNLOAD_ROW_LIMIT);
      }
      const isDownloadTruncated = Boolean(downloadTruncatedRows);

      this.update({
        columnGroups,
        rows: isDownloadTruncated ? downloadTruncatedRows : rows,
        segmentMetadata,
        isDownloadTruncated,
        sortConfig,
        segmentationLimit,
      });

      /**
       * TODO @pablofierro
       *
       * Table data is computed entirely on the lowest component in the report visualization hierarchy (chart widgets), this leads to
       * data dependencies going upwards and downwards from chart widgets -> ReportVisualization -> SegmentFilterDataTable, in order
       * to have a global table sorting component, we need a reference to the table data being presented to the user, that's why we
       * call `segmentFilterController.loadSections` but this is not an ideal data flow since this `InsightsTableChart` widget is
       * itself rendering the table component and not reusing the `ReportVisualization` abstractions to render a data table.
       *
       * The above seems to break the abstractions we made for `ReportVisualization` being responsible for rendering
       * and displaying table data, a follow up is: how can we improve data flow dependencies so that `ReportVisualization`
       * has access to the table data a report needs (for a global sorting component) without doing cross-component communication
       * with this `segmentFilterController` context call below
       */
      this.segmentFilterController.loadSections(
        [
          {
            items: [],
            metadata: {},
            tableColumns: columnGroups,
            apiResultsTruncated: false,
          },
        ],
        {
          defaultSortConfig,
          tableSortConfig: sortConfig,
          defaultNumSelections: 0,
          maxNumSelections: 0,
        },
      );
    }
  }

  handleCellClick = (ev: CellClickEvent<any, InsightsTableColumnContext>) => {
    const {row, target, context, column: clickedColumn} = ev.detail;
    // construct segmentation path for row, avoids any issues with comma characters in the segment values
    const path = this.params.results.headers
      .slice(1)
      .map((_, index) => row[`${CommonDataTableFields.SegmentPrefix}${index}`]);
    const dataColumns = flattenColumns(this.state.columnGroups).filter(
      (column) => column.type === TableColumnType.Data,
    );

    let metricKey = clickedColumn.context?.metricKey;

    // for single metric tables, allow popover actions on the main metric for the query
    if (metricKey === undefined && dataColumns.length === 1) {
      metricKey = dataColumns[0].context.metricKey;
    }

    this.update({
      popoverState: {
        path,
        metricKey,
        isTimeComparisonMetric: clickedColumn.context?.isTimeComparison,
        targetCell: target,
        columnIndex: context.columnIdx,
        rowIndex: context.rowIdx,
        columnType: clickedColumn.type,
        virtualElementPosition: {
          contextElement: target,
          getBoundingClientRect() {
            const targetRect = target.getBoundingClientRect();
            const rowHeight = Themes[Theme.Standalone].rowHeight;

            // for anything greater than 4 row sizes we will present the
            // context menu in the position of the cursor, to prevent
            // the entry point for the menu changing considerably
            if (targetRect.height >= rowHeight * 4) {
              return {
                width: 0,
                height: 0,
                left: ev.detail.mouseX,
                top: ev.detail.mouseY,
              } as ClientRect;
            }

            // otherwise we present the context menu following the cursor along
            // the horizontal axis of table rows, but anchored to a specific table cell
            // along the vertical axis
            return {
              height: targetRect.height,
              width: targetRect.width,
              left: ev.detail.mouseX,
              top: targetRect.top,
            } as ClientRect;
          },
        },
      },
    });
  };

  handlePopoverAction(ev) {
    const {isTimeComparisonMetric, columnIndex, columnType} = this.state.popoverState;
    this.params.popoverActionCallback({
      columnType,
      columnIndex: columnType === TableColumnType.Segment ? columnIndex : null,
      segment: this.getPopoverMetricPath(),
      action: ev.detail.action,
      isTimeComparisonSegment: isTimeComparisonMetric,
      color: null,
    });

    this.handlePopoverClose();
  }

  handlePopoverClose() {
    this.update({popoverState: null});
  }

  getPopoverMetricPath() {
    const {metricKey, path} = this.state.popoverState;
    return [metricKey ?? `$event`, ...path];
  }

  getPopoverMenuItems() {
    return this.params.popoverCallback(null, this.getPopoverMetricPath(), {
      forSegmentColumn: this.state.popoverState.metricKey === undefined,
      isTimeComparisonSegment: this.state.popoverState.isTimeComparisonMetric,
    });
  }
}

export const InsightsTableChartEl = defineAndMakeJsxFactory<InsightsTableJsxProps>(
  `mp-insights-table-chart`,
  InsightsTableChart,
);
export const InsightsTableChartReact = panel2react<InsightsTableJsxProps>(
  `mp-insights-table-chart`,
  InsightsTableChart,
);
